/**
 * Central company facts for Narayanee Clothing Pvt. Ltd.
 * Source of truth: company profile (October 2026).
 * Edit this file — and the modules in src/data — rather than page copy.
 */

export const company = {
  legalName: "Narayanee Clothing Pvt. Ltd.",
  shortName: "NCPL",
  /**
   * Verified registered office only. Empty pin and state stay omitted.
   * South Twenty Four Parganas is the district supplied with this address.
   */
  registeredOffice: {
    premises: "2nd Floor & 3rd Floor",
    holding: "B3-66A/B to E/New",
    street: "Budge Budge Trunk Road",
    area: "Maheshtala",
    city: "Kolkata",
    district: "South Twenty Four Parganas",
    state: null,
    pin: "700141",
    country: "India",
  },
  /** Corporate Identification Number. Footer only. */
  cin: "U18109WB2021PTC244432",
  /** GSTIN. Omit from the UI while null. */
  gstin: null,
  established: 1994,
  founder: {
    name: "Pawan Jhunjhunwala",
    role: "Founder & Director",
  },
  category: "Specialist manufacturer of men’s and kids’ woven shirts",
  positioning:
    "A specialist woven shirt manufacturer in Kolkata, serving large retail and private-label programmes across men’s and kids’ shirts, and building international sourcing partnerships.",
  description:
    "Narayanee Clothing manufactures men’s and kids’ woven shirts for leading retail and private-label programmes across India. From product development and CAD to automated cutting, sewing, finishing and packing, we combine scale, speed and disciplined execution.",
  originStory:
    "What started with a production capacity of 50 shirts a day now has a production capacity of 9 million shirts a year.",
  commitment:
    "Production is organised around the buyer’s specification, the approved sample and the discipline of a repeat order.",
  standing:
    "Three decades of large-scale shirt manufacturing in Kolkata, now building international sourcing partnerships.",
  tagline: "Shirts Made at Scale. Quality Built Into Every Step.",
  units: 3,
  unitsWord: "three",
  unitsHeading: "Three manufacturing units in Kolkata",
  unitsDescription: "Three manufacturing units in Kolkata.",
  startedWith: {
    value: "50",
    unit: "shirts a day",
  },
  annualCapacity: {
    value: "9 million+",
    approximate: true,
    unit: "garments per year",
  },
  cuttingCapacity: {
    value: "9 million",
    unit: "pcs/year",
  },
  sewingCapacity: {
    value: "7.5 million",
    unit: "pcs/year",
  },
  finishingCapacity: {
    value: "9 million",
    unit: "pcs/year",
  },
  machines: {
    value: "1,300",
    type: "Manual and computerised",
  },
  brandsServed: {
    value: "10+",
    unit: "brands",
  },
  onTimeDelivery: {
    value: "90%",
    label: "On-time delivery",
  },
  commercial: {
    moq: "2,000 pieces per colour, per style",
    leadTime: "60–120 days",
    aql: "AQL 2.5 (ISO 2859-1)",
    aqlDetail:
      "Pre-final inspection is carried out to an Acceptance Quality Limit of 2.5 under ISO 2859-1, General Inspection Level II, before shipment.",
    orderSize:
      "Orders served so far run from 2,000 to 80,000 pieces. Larger orders are welcome.",
    consistency:
      "Our three-unit setup supports both multi-style retail programmes and large-volume production runs.",
  },
  compliance: {
    name: "SEDEX SMETA 4-Pillar",
    shortName: "SMETA 4-Pillar",
    type: "Ethical trade and social compliance audit framework",
    sedexMember: true,
    /**
     * Official Sedex member logo key in src/config/images.js.
     * Positive mark on light backgrounds; negative mark on dark backgrounds.
     */
    sedexLogoAsset: "sedexMember",
    auditor: "Intertek India",
    dateDisplay: "12 September 2026",
    teaser:
      "SMETA 4-Pillar audited across labour standards, health and safety, environment and business ethics.",
    // Public summary omits factory names and addresses.
    // An earlier sentence named one unit, its address and a Sedex site reference.
    // That unit-specific scope stays off the public site pending NCPL review.
    summary:
      "A SMETA 4-Pillar follow-up audit by Intertek India on 12 September 2026 covered labour standards, health and safety, environment and business ethics. SMETA is an audit used with Sedex. It is not a product-quality certificate.",
    areas: [
      { id: "labour", title: "Labour Standards" },
      { id: "safety", title: "Health & Safety" },
      { id: "environment", title: "Environment" },
      { id: "ethics", title: "Business Ethics" },
    ],
  },
  location: {
    city: "Kolkata",
    state: "West Bengal",
    country: "India",
    display: "Kolkata, West Bengal, India",
  },
  /**
   * Public contact. Null values are omitted from the site.
   * Do not substitute personal emails, personal mobiles, or factory addresses.
   * Phone stays unpublished. When set, use { display, href }.
   */
  contact: {
    businessEmail: "info@ncplshirts.com",
    phone: null,
    whatsapp: {
      number: null,
      showInUi: false,
    },
    linkedin: null,
  },
  social: [],
};

/** One public address. Blank parts are omitted so a missing PIN or state is not shown. */
export function formatRegisteredOffice(office = company.registeredOffice) {
  if (!office) return "";
  if (typeof office === "string") return office;
  const cityLine = [office.city, office.district].filter(Boolean).join(", ");
  const regionLine = [office.state, office.country].filter(Boolean).join(", ");
  return [
    office.premises,
    office.holding,
    office.street,
    office.area,
    cityLine,
    office.pin,
    regionLine,
  ]
    .filter(Boolean)
    .join("\n");
}

/** Public facts used across the site. Change them here, not in page copy. */
export const companyFacts = {
  founded: company.established,
  units: company.units,
  monthlyShirtCapacity: "~800K",
  workforce: "1,800+",
  location: company.location.display,
  compliance: "SMETA 4-Pillar Audited",
  integratedCapability: "End-to-End",
};

export const site = {
  url: (import.meta.env.VITE_SITE_URL || "").replace(/\/$/, ""),
  name: company.legalName,
  defaultTitle:
    "Men’s Shirt Manufacturer & Private Label Partner in India | NCPL",
  defaultDescription:
    "Narayanee Clothing manufactures men’s and kids’ woven shirts in India, with large-scale production, product development, CAD, automated cutting, quality control and private-label manufacturing capabilities.",
};
